'use client';

import Link from 'next/link';

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { ArrowRightIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { useUpdateUser } from '@kit/supabase/hooks/use-update-user-mutation';
import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Heading } from '@kit/ui/heading';
import { Input } from '@kit/ui/input';
import { Trans } from '@kit/ui/trans';

import { PasswordResetSchema } from '../schemas/password-reset.schema';

export function UpdatePasswordForm({
  redirectTo,
  showWelcomeScreen = false,
}: {
  redirectTo: string;
  showWelcomeScreen?: boolean;
}) {
  const updateUser = useUpdateUser();

  const form = useForm<z.infer<typeof PasswordResetSchema>>({
    resolver: zodResolver(PasswordResetSchema),
    defaultValues: {
      password: '',
      repeatPassword: '',
    },
  });

  if (updateUser.error) {
    return <ErrorState onRetry={() => updateUser.reset()} />;
  }

  if (updateUser.data && !updateUser.isPending) {
    return (
      <SuccessState
        redirectTo={redirectTo}
        showWelcomeScreen={showWelcomeScreen}
      />
    );
  }

  return (
    <div className={'flex w-full flex-col space-y-6'}>
      <div className={'flex flex-col justify-center space-y-4'}>
        <Heading level={3} className={'text-center tracking-tight'}>
          <Trans
            i18nKey={
              showWelcomeScreen
                ? 'common:nrf:welcomeTitle'
                : 'auth:passwordResetLabel'
            }
          />
        </Heading>
        {showWelcomeScreen && (
          <div className="text-center">
            <Trans i18nKey={'common:nrf:welcomeText'} />
          </div>
        )}
      </div>
      <Form {...form}>
        <form
          className={'flex w-full flex-1 flex-col'}
          onSubmit={form.handleSubmit(({ password }) => {
            return updateUser.mutateAsync({
              password,
              data: { nrf_has_to_change_password: false },
              redirectTo: redirectTo,
            });
          })}
        >
          <div className={'flex-col space-y-4'}>
            <FormField
              name={'password'}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey={'common:password'} />
                  </FormLabel>

                  <FormControl>
                    <Input required type="password" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name={'repeatPassword'}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey={'common:repeatPassword'} />
                  </FormLabel>

                  <FormControl>
                    <Input required type="password" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              disabled={updateUser.isPending}
              type="submit"
              className={'w-full'}
            >
              <Trans
                i18nKey={
                  showWelcomeScreen
                    ? 'auth:passwordSetLabel'
                    : 'auth:passwordResetLabel'
                }
              />
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

function SuccessState({
  redirectTo,
  showWelcomeScreen = false,
}: {
  redirectTo: string;
  showWelcomeScreen?: boolean;
}) {
  return (
    <div className={'flex flex-col space-y-4'}>
      <Alert variant={'success'}>
        {/* <CheckIcon className={'s-6'} /> */}

        <AlertTitle>
          <Trans
            i18nKey={
              showWelcomeScreen
                ? 'account:setPasswordSuccess'
                : 'account:updatePasswordSuccess'
            }
          />
        </AlertTitle>

        <AlertDescription>
          <Trans
            i18nKey={
              showWelcomeScreen
                ? 'account:setPasswordSuccessMessage'
                : 'account:updatePasswordSuccessMessage'
            }
          />
        </AlertDescription>
      </Alert>

      <Link href={redirectTo}>
        <Button variant={'outline'} className={'w-full'}>
          <span>
            <Trans i18nKey={'common:backToHomePage'} />
          </span>

          <ArrowRightIcon className={'ml-2 h-4'} />
        </Button>
      </Link>
    </div>
  );
}

function ErrorState(props: { onRetry: () => void }) {
  return (
    <div className={'flex flex-col space-y-4'}>
      <Alert variant={'destructive'}>
        <ExclamationTriangleIcon className={'s-6'} />

        <AlertTitle>
          <Trans i18nKey={'common:genericError'} />
        </AlertTitle>

        <AlertDescription>
          <Trans i18nKey={'auth:resetPasswordError'} />
        </AlertDescription>
      </Alert>

      <Button onClick={props.onRetry} variant={'outline'}>
        <Trans i18nKey={'common:retry'} />
      </Button>
    </div>
  );
}
